import { NAVBAR_HEIGHT } from '@/shared/lib/util/constants';
import { SideMenuContext } from '@/shared/model';
import { PROFILE_TRIGGER_ATTR, useSidebarStore } from '@/shared/model/SidebarStore';
import { PROFILE_MENU_PAPER_ID } from '@/shared/ui/components/ProfileMenu';
import { BaseSideMenuSideNav, SIDE_NAV_EXPANDED_WIDTH, SIDE_NAV_RAIL_WIDTH } from '@/shared/ui/components/SideMenus/BaseSideMenuSideNav';
import { NAV_COLORS } from '@/shared/ui/components/SideMenus/navPalette';
import type { DrawerProps } from '@mui/material';
import { Drawer } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { MouseEvent } from 'react';
import { useCallback, useContext, useEffect, useRef } from 'react';

type Props = DrawerProps & {
  topOffset?: number;
};

// Small delays keep the nav from flickering open when the pointer only crosses the rail on its
// way somewhere else, and keep it open long enough to travel from the rail onto a menu item.
const HOVER_OPEN_DELAY_MS = 120;
const HOVER_CLOSE_DELAY_MS = 250;

/*
 * The profile popup opens up and to the right of the avatar, so the way to it crosses a strip of
 * rail that is not the avatar.  Treating that strip as "the rest of the nav" closed the popup the
 * moment the pointer left the avatar.  So, while the popup is open, the nav only takes the pointer
 * back when it is clearly heading somewhere else:
 *
 * - within PROFILE_NEAR_PX of the popup counts as on it;
 * - so does anywhere inside the triangle from where the pointer last left the avatar to the
 *   popup's near edge -- the route any diagonal move towards it takes (the "safe triangle"
 *   menus use for submenus);
 * - and anything else only closes the popup after PROFILE_CLOSE_DELAY_MS, re-checked when the
 *   timer fires, so a sloppy path that clips the edge of the triangle is forgiven.
 */
const PROFILE_NEAR_PX = 24;
const PROFILE_CLOSE_DELAY_MS = 200;

type Point = { x: number; y: number };

const insideTriangle = (p: Point, a: Point, b: Point, c: Point) => {
  const side = (p1: Point, p2: Point, p3: Point) => (p1.x - p3.x) * (p2.y - p3.y) - (p2.x - p3.x) * (p1.y - p3.y);
  const d1 = side(p, a, b);
  const d2 = side(p, b, c);
  const d3 = side(p, c, a);
  const hasNegative = d1 < 0 || d2 < 0 || d3 < 0;
  const hasPositive = d1 > 0 || d2 > 0 || d3 > 0;
  return !(hasNegative && hasPositive);
};

const nearRect = (p: Point, rect: DOMRect, margin: number) =>
  p.x >= rect.left - margin && p.x <= rect.right + margin && p.y >= rect.top - margin && p.y <= rect.bottom + margin;

const NavigationSidebarMenu = ({ topOffset = 0, ...props }: Props) => {
  const theme = useTheme();
  const { drawerOpen, setDrawerOpen, content, isPinned, isAutoHideEnabled, setHoverExpanded, isMobileOrTablet } =
    useContext(SideMenuContext);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const profileCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Where the pointer last was on the avatar: the apex of the safe triangle.
  const lastOnTrigger = useRef<Point | null>(null);
  const lastPointer = useRef<Point | null>(null);

  const clearHoverTimer = useCallback(() => {
    if (hoverTimer.current) {
      clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
  }, []);

  const scheduleHover = useCallback(
    (expanded: boolean) => {
      if (isPinned || !isAutoHideEnabled || isMobileOrTablet) return;
      clearHoverTimer();
      hoverTimer.current = setTimeout(
        () => {
          // Entering the nav by way of the avatar opens the profile popup, not the menu.
          if (expanded && useSidebarStore.getState().profileMenuAnchor) return;
          setHoverExpanded(expanded);
        },
        expanded ? HOVER_OPEN_DELAY_MS : HOVER_CLOSE_DELAY_MS
      );
    },
    [isPinned, isAutoHideEnabled, isMobileOrTablet, clearHoverTimer, setHoverExpanded]
  );

  // Pinning mid-hover must not leave a pending timer that collapses the nav a moment later.
  useEffect(() => {
    if (isPinned) clearHoverTimer();
  }, [isPinned, clearHoverTimer]);

  useEffect(() => clearHoverTimer, [clearHoverTimer]);

  const clearProfileCloseTimer = useCallback(() => {
    if (profileCloseTimer.current) {
      clearTimeout(profileCloseTimer.current);
      profileCloseTimer.current = null;
    }
  }, []);

  useEffect(() => clearProfileCloseTimer, [clearProfileCloseTimer]);

  /** Whether the pointer is on, near, or on its way to the open profile popup. */
  const isHeadingForProfilePopup = useCallback((p: Point) => {
    const { profileMenuAnchor } = useSidebarStore.getState();
    const paper = document.getElementById(PROFILE_MENU_PAPER_ID);
    if (!profileMenuAnchor || !paper) return false;
    if (profileMenuAnchor.matches(':hover') || paper.matches(':hover')) return true;

    const paperRect = paper.getBoundingClientRect();
    if (nearRect(p, paperRect, PROFILE_NEAR_PX) || nearRect(p, profileMenuAnchor.getBoundingClientRect(), PROFILE_NEAR_PX / 2)) return true;

    const apex = lastOnTrigger.current;
    if (!apex) return false;
    // The popup's edge facing the avatar, padded so the triangle is a little generous.
    const edgeX = paperRect.left <= apex.x ? paperRect.right : paperRect.left;
    return insideTriangle(p, apex, { x: edgeX, y: paperRect.top - PROFILE_NEAR_PX }, { x: edgeX, y: paperRect.bottom + PROFILE_NEAR_PX });
  }, []);

  // While unpinned the paper floats over the page instead of pushing it, so it gets a shadow.
  const isOverlaying = !isPinned && drawerOpen;

  return (
    <Drawer
      variant={isMobileOrTablet ? 'temporary' : 'permanent'}
      anchor="left"
      open={drawerOpen}
      onClose={() => setDrawerOpen(false)}
      ModalProps={{ keepMounted: true }}
      slotProps={{
        paper: {
          onMouseEnter: () => scheduleHover(true),
          onMouseLeave: () => scheduleHover(false),
          // With the profile popup open, moving on to another part of the nav closes the popup and
          // gives the nav its hover back -- but not while the pointer is heading for the popup.
          // (A pinned menu the popup closed reopens itself.)
          onMouseMove: (event: MouseEvent<HTMLElement>) => {
            const p = { x: event.clientX, y: event.clientY };
            lastPointer.current = p;
            if (!useSidebarStore.getState().profileMenuAnchor) return;

            if ((event.target as Element).closest(`[${PROFILE_TRIGGER_ATTR}]`)) {
              lastOnTrigger.current = p;
              clearProfileCloseTimer();
              return;
            }
            if (isHeadingForProfilePopup(p)) {
              clearProfileCloseTimer();
              return;
            }
            if (profileCloseTimer.current) return;
            const navPaper = event.currentTarget;
            profileCloseTimer.current = setTimeout(() => {
              profileCloseTimer.current = null;
              const { profileMenuAnchor, setProfileMenuAnchor } = useSidebarStore.getState();
              if (!profileMenuAnchor || (lastPointer.current && isHeadingForProfilePopup(lastPointer.current))) return;
              setProfileMenuAnchor(null);
              lastOnTrigger.current = null;
              // Only if the pointer is still on the nav; it may have left for the page meanwhile.
              if (navPaper.matches(':hover')) scheduleHover(true);
            }, PROFILE_CLOSE_DELAY_MS);
          },
          sx: {
            // Page content is free to stack up to the drawer level -- the patient header is sticky
            // at zIndex.drawer - 1 -- so an overlaying nav has to sit at the drawer level itself.
            // At 10 it slid underneath, which only showed while unpinned, since a pinned nav
            // reserves its own width and never overlaps anything.
            zIndex: theme.zIndex.drawer,
            overflow: 'hidden',
            width: drawerOpen ? SIDE_NAV_EXPANDED_WIDTH : SIDE_NAV_RAIL_WIDTH,
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.shortest
            }),
            boxShadow: isOverlaying ? theme.shadows[8] : 'none',
            backgroundColor: NAV_COLORS.pane,
            color: NAV_COLORS.onDark,
            borderRight: 'none',
            ...(isMobileOrTablet && { width: SIDE_NAV_EXPANDED_WIDTH }),
            '& .MuiListSubheader-root': {
              color: NAV_COLORS.onDark,
              backgroundColor: NAV_COLORS.pane
            },

            '& .MuiDrawer-paper::-webkit-scrollbar-thumb': {
              backgroundColor: alpha(NAV_COLORS.onDark, 0.6),
              borderRight: `2px ${NAV_COLORS.pane} solid`,
              backgroundClip: 'padding-box',
              borderRadius: 8
            },
            '& .MuiDrawer-paper::-webkit-scrollbar': {
              width: 9,
              backgroundColor: NAV_COLORS.pane
            },
            '& .MuiDrawer-paper::-webkit-scrollbar-track': {
              backgroundColor: NAV_COLORS.pane
            },
            '& .MuiListItemButton-root': {
              paddingLeft: 1.5,
              paddingRight: 1,
              color: NAV_COLORS.onDarkMuted,
              '& .MuiSvgIcon-root': {
                color: NAV_COLORS.onDarkMuted
              },
              '&:hover': {
                backgroundColor: `var(--nav-hover, ${NAV_COLORS.hover})`,
                color: NAV_COLORS.onDark,
                '& .MuiSvgIcon-root': {
                  color: NAV_COLORS.onDark
                }
              },
              // Selected inverts: the ground becomes the text and the text becomes the ground.
              '&.Mui-selected, &.Mui-selected:hover': {
                backgroundColor: NAV_COLORS.onDark,
                color: NAV_COLORS.ink,
                '& .MuiSvgIcon-root': {
                  color: NAV_COLORS.ink
                }
              }
            }
          }
        }
      }}
      {...props}
    >
      <BaseSideMenuSideNav
        sx={{
          marginTop: isMobileOrTablet ? 0 : `${NAVBAR_HEIGHT + topOffset}px`,
          height: isMobileOrTablet ? '100%' : `calc(100% - ${NAVBAR_HEIGHT + topOffset}px)`,
          minHeight: 0,
          paddingRight: drawerOpen ? 2 : 0
        }}
        drawerOpen={drawerOpen}
      >
        {content}
      </BaseSideMenuSideNav>
    </Drawer>
  );
};

export default NavigationSidebarMenu;
